import {useDisclosure} from '@mantine/hooks';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState, useEffect} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {STORABLE_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import LLMFeedbackModal from 'llm/feedback/LLMFeedbackModal';
import {GRAY_DARKER, GRAY} from 'ui-library/Colors';

export interface AnnotationAssistantFeedbackProps {
  userPrompt: string;
  traceId: string;
}

export default function AnnotationAssistantFeedback({
  userPrompt,
  traceId,
}: AnnotationAssistantFeedbackProps): JSX.Element {
  const [usefulSuggestion, setUsefulSuggestion] = useState<boolean | null>(null);
  const [feedbackModalOpened, {open: openFeedbackModal, close: closeFeedbackModal}] = useDisclosure(false);

  useEffect(() => {
    // If there is a new suggest, reset the useful state.
    setUsefulSuggestion(null);
  }, [traceId]);

  return (
    <>
      <div className={css(styles.feedback)}>
        <p>Was this suggestion helpful?</p>
        <a
          aria-label="Yes"
          role="button"
          tabIndex={0}
          className={classNames(
            'fa fa-thumbs-up',
            css(styles.thumbsLink, usefulSuggestion !== true && styles.thumbsLinkInactive)
          )}
          onClick={() => {
            if (usefulSuggestion === true) {
              setUsefulSuggestion(null);
            } else {
              trackEvent(STORABLE_ANALYTICS_ACTIONS.SUBMITTED_ROLE_ANNOTATION_AI_SUGGESTION_USEFULNESS, {
                useful: true,
                traceId,
              });
              setUsefulSuggestion(true);
            }
          }}
        />
        <a
          aria-label="No"
          role="button"
          tabIndex={0}
          className={classNames(
            'fa fa-thumbs-down',
            css(styles.thumbsLink, usefulSuggestion !== false && styles.thumbsLinkInactive)
          )}
          onClick={() => {
            if (usefulSuggestion === false) {
              setUsefulSuggestion(null);
            } else {
              trackEvent(STORABLE_ANALYTICS_ACTIONS.SUBMITTED_ROLE_ANNOTATION_AI_SUGGESTION_USEFULNESS, {
                useful: false,
                traceId,
              });
              setUsefulSuggestion(false);
            }
          }}
        />
        {usefulSuggestion === false && (
          <div>
            <a
              role="button"
              tabIndex={0}
              className={css(styles.feedbackModalLink)}
              onClick={openFeedbackModal}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  openFeedbackModal();
                }
              }}
            >
              Tell us more
            </a>
          </div>
        )}
      </div>

      <LLMFeedbackModal
        opened={feedbackModalOpened}
        onRequestClose={closeFeedbackModal}
        title="Role Plate Annotator feedback"
        optionalContextItems={[{name: 'prompt', value: userPrompt}]}
        requiredContextItems={[
          {
            name: 'Useful',
            value: String(usefulSuggestion),
          },
          {
            name: 'Trace ID',
            value: traceId,
          },
        ]}
      />
    </>
  );
}

const styles = StyleSheet.create({
  feedback: {
    color: GRAY_DARKER,
    textAlign: 'right',
  },
  thumbsLink: {
    fontSize: 14,
    display: 'inline',
    ':hover': {
      textDecoration: 'none',
    },
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    padding: '2px 4px',
    marginRight: '4px',
  },
  thumbsLinkInactive: {
    color: GRAY,
  },
  feedbackModalLink: {
    marginLeft: 6,
  },
});
